/*
 * Copyright (c) Nexign, JSC, 2019-2024.
 */
/**
 * Root saga
 *
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @version 1.28.0
 * @since 1.0.0
 */

import { SagaIterator } from '@redux-saga/core';
import { all, fork } from 'redux-saga/effects';

import { headerSaga } from '../components/Header/headerSaga';
import { tableSagas } from '../components/Table/sagas/tableSagas';
import { GTMSaga } from '../modules/analytics/GoogleTagManager/GTMSagas';
import { appSagas } from '../modules/app/appSagas';
import { asyncStateManagementSagas } from '../modules/asyncStateManagment/asyncStateManagementSagas';
import { bannerSagas } from '../modules/banner/bannerSagas';
import { breadCrumbsSagas } from '../modules/breadCrumbs/breadCrumbsSagas';
import { captchaSagas } from '../modules/captcha/captchaSagas';
import { densitySagas } from '../modules/density/densitySagas';
import { exportSagas } from '../modules/exports/exportSagas';
import { intSagas } from '../modules/internationalization/intSagas';
import { loggingSaga } from '../modules/logging/loggingSaga';
import { modalSagas } from '../modules/modal/modalSaga';
import { notificationSagas } from '../modules/notification/notificationSagas';
import { onboardingSagas } from '../modules/onboarding/onboardingSagas';
import { personalDataConfirmSagas } from '../modules/personalDataConfirm/personalDataConfirmSaga';
import { watchPollSaga } from '../modules/polling/pollingSaga';
import { promisedPaymentSagas } from '../modules/promisedPayment/promisedPaymentSagas';
import { guardSagas } from '../modules/role/sagas/checkGuardSaga';
import { storageSaga } from '../modules/storage/storageSagas';
import { initSuperSearchSaga } from '../modules/superSearch/superSearchSagas';
import { wizardSagas } from '../modules/wizard/wizardSagas';
import { rootAuthSaga } from '../pages/PageAuth/sagas/authSaga';
import { billsSagas } from '../pages/PageBills/billsSagas';
import { widgetSaga } from '../pages/PageDashboard/components/Widgets/widgetSagas';
import { expensesAnalyzerSagas } from '../pages/PageExpensesAnalyzer/pageExpensesAnalyzerSaga';
import { pagaHistoryInquirySagas } from '../pages/PageHistoryInquiry/pageHistoryInquirySagas';
import { settingsSaga } from '../pages/PageSettings/settingsSagas';
import { initSubscriberExtFilter } from '../pages/PageSubscriberMobile/components/SubscriberMobileExtFilter/sagas';

/**
 * @description Aggregator of all existing sags
 * @returns {SagaIterator}
 */
function* rootSaga(): SagaIterator {
    yield all([
        fork(appSagas),
        fork(asyncStateManagementSagas),
        fork(bannerSagas),
        fork(breadCrumbsSagas),
        fork(billsSagas),
        fork(pagaHistoryInquirySagas),
        fork(captchaSagas),
        fork(densitySagas),
        fork(expensesAnalyzerSagas),
        fork(exportSagas),
        fork(GTMSaga),
        fork(guardSagas),
        fork(headerSaga),
        fork(initSubscriberExtFilter),
        fork(initSuperSearchSaga),
        fork(intSagas),
        fork(loggingSaga),
        fork(modalSagas),
        fork(notificationSagas),
        fork(onboardingSagas),
        fork(personalDataConfirmSagas),
        fork(promisedPaymentSagas),
        fork(rootAuthSaga),
        fork(settingsSaga),
        fork(storageSaga),
        fork(tableSagas),
        fork(watchPollSaga),
        fork(widgetSaga),
        fork(wizardSagas),
    ]);
}

export default rootSaga;
